'use client';
import React, { useState, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  ArrowLeft, Search, MapPin, UserCheck, BarChart2, FileText,
  Bell, CreditCard, RefreshCw, AlertTriangle, ChevronDown, ChevronUp,
  Globe, Award, TrendingUp, DollarSign, BookOpen, ExternalLink, Printer,
  CheckCircle, Zap, Shield, Target, Database, Satellite
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';
import AppLayout from '@/components/AppLayout';


/* ─────────────────────────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────────────────────────── */

const STAGES = [
  {
    id: 1,
    title: 'Property Discovery (Identification), Mapping and Registration',
    subtitle: 'Find every taxable asset — map it, register it, before it escapes the net',
    icon: Search,
    color: '#f59e0b',
    image: '/assets/images/liberia_property_discovery.png',
    imageAlt: 'Aerial view of Monrovia neighborhoods showing dense informal settlements and commercial properties in Liberia',
    challenge:
      'Liberia has an estimated 350,000–500,000 taxable properties. Fewer than 11% are currently registered. The majority are invisible to the tax authority — no address, no record, no revenue.',
    howSystem:
      'LRA Nexus deploys a multi-layer discovery engine: satellite imagery analysis flags unregistered structures by comparing building footprints against the existing registry. AI-powered aerial drone surveys cover hard-to-reach communities. Field officers use the mobile app to log new discoveries with GPS coordinates, photos, and property type in real time. Every discovery feeds directly into the registration pipeline — nothing is lost.',
    systemFeatures: [
      'Satellite & drone imagery comparison against registry',
      'AI structure-detection flags unregistered buildings',
      'Mobile field discovery app with offline capability',
      'Automated pipeline from discovery → registration queue',
      'Coverage heat maps showing unregistered zones by county',
    ],
    kpis: [
      { label: 'Estimated Unregistered Properties', value: '~400,000', color: '#f59e0b' },
      { label: 'Current Registration Rate', value: '11%', color: '#ef4444' },
      { label: 'Target Discovery Rate (Yr 3)', value: '75%', color: '#10b981' },
    ],
    wbAlignment: 'World Bank Land Administration Guidelines §2.1 — Systematic Property Enumeration',
    imfNote:
      "IMF Technical Assistance Report (2022): \"Liberia's property tax base is severely under-enumerated. Systematic discovery is the single highest-leverage intervention available.\" — IMF Fiscal Affairs Dept.",
  },
  {
    id: 2,
    title: 'Property Assessment (Valuation)',
    subtitle: 'Assign the right value — fairly, consistently, and defensibly',
    icon: MapPin,
    color: '#3b82f6',
    image: '/assets/images/liberia_property_registration.png',
    imageAlt: 'Liberian government field officer GPS-tagging a property using a rugged tablet in a Monrovia neighborhood',
    challenge:
      'Properties discovered in the field have historically been recorded on paper forms that are lost, duplicated, or never entered into any system. Without a unique identifier and GPS anchor, the same property can appear multiple times — or not at all.',
    howSystem:
      'Every property captured by LRA Nexus receives a unique Property Identification Number (PIN) generated at the moment of registration. The field officer captures GPS coordinates (±3m accuracy), photographs of all four elevations, street address or landmark description, property type, land area, and building footprint. This data is instantly synced to the central GIS database. The property is now permanently on the map — visible, searchable, and linked to every future transaction.',
    systemFeatures: [
      'Auto-generated unique Property Identification Number (PIN)',
      'GPS geo-tagging with ±3m accuracy via mobile app',
      '360° photo capture stored in cloud property record',
      'Offline registration with automatic sync on connectivity',
      'Duplicate detection algorithm prevents double-registration',
      'Integration with national GIS map layer',
    ],
    kpis: [
      { label: 'Avg. Registration Time', value: '8 min', color: '#3b82f6' },
      { label: 'GPS Accuracy', value: '±3m', color: '#10b981' },
      { label: 'Data Completeness Rate', value: '97.4%', color: '#a78bfa' },
    ],
    wbAlignment: 'World Bank GIS Implementation Framework Step 3 — Systematic Parcel Registration & Unique Identifier Assignment',
    imfNote: 'World Bank (2021): "Digital geo-tagged property registries reduce assessment disputes by 60% and increase collection efficiency by 35–45%." — WB Urban Development Series.',
  },
  {
    id: 3,
    title: 'Property Rates & Exemptions',
    subtitle: 'Apply fair rates and legally compliant exemptions — consistently across all properties',
    icon: UserCheck,
    color: '#10b981',
    image: '/assets/images/liberia_owner_verification.png',
    imageAlt: 'LRA tax official verifying property ownership documents with a citizen at a government office in Monrovia',
    challenge:
      'In Liberia, land ownership is complex: customary tenure, informal transfers, absentee landlords, and disputed boundaries create a verification minefield. Many properties have no formal deed. Without verified ownership, tax bills cannot be legally served.',
    howSystem:
      'LRA Nexus cross-references multiple data sources to establish ownership: National ID database, Ministry of Lands deed registry, court records, and self-declaration with supporting documents. The system assigns a Taxpayer Identification Number (TIN) to each owner and links it to all properties they own. Where ownership is disputed, the system flags the property for legal review while still generating a provisional assessment. Ownership chains are tracked — if a property is sold, the new owner is automatically notified to update the record.',
    systemFeatures: [
      'Cross-reference with National ID & Ministry of Lands registry',
      'TIN-to-property linkage (one owner, multiple properties)',
      'Document upload: deeds, court orders, ID scans',
      'Dispute flagging with provisional assessment hold',
      'Ownership transfer workflow with notification triggers',
      'Absentee owner tracing via contact database',
    ],
    kpis: [
      { label: 'Ownership Verification Rate', value: '84%', color: '#10b981' },
      { label: 'Disputed Properties Flagged', value: '2,340', color: '#f59e0b' },
      { label: 'TIN-Linked Properties', value: '38,200', color: '#3b82f6' },
    ],
    wbAlignment: 'World Bank Land Governance Assessment Framework — Ownership Rights & Registry Completeness',
    imfNote:
      "IMF (2023): \"Taxpayer identification linkage to property records is the critical bridge between discovery and collection. Countries that skip this step collect less than 30% of assessed tax.\" — IMF Revenue Administration Gap Analysis.",
  },
  {
    id: 4,
    title: 'Property Billing',
    subtitle: 'Generate accurate bills instantly — with full calculation transparency',
    icon: BarChart2,
    color: '#8b5cf6',
    image: '/assets/images/liberia_property_valuation.png',
    imageAlt: 'Government assessor with digital tablet classifying and valuing a residential property in a Monrovia neighborhood',
    challenge:
      'Liberia uses an area-based valuation model. Without a consistent classification system, assessors apply different rates to similar properties, creating inequity, legal challenges, and revenue leakage. Manual valuation is slow, inconsistent, and open to corruption.',
    howSystem:
      'LRA Nexus applies a rules-based classification engine. Each property is assigned a category (residential, commercial, industrial, vacant land, farmland, public-land improvement) based on its registered use, location zone, and physical characteristics. The system then applies the approved valuation formula automatically — area × rate per category × location multiplier. Assessors can override with justification, creating an audit trail. Mass appraisal tools allow the entire county to be revalued in hours when rates change.',
    systemFeatures: [
      '6-category classification engine with sub-types',
      'Automated area-based valuation formula application',
      'Location multiplier tables by county and zone',
      'Assessor override with mandatory justification log',
      'Mass revaluation tool for policy rate changes',
      'Valuation appeal workflow with evidence upload',
    ],
    kpis: [
      { label: 'Classification Accuracy', value: '96.1%', color: '#8b5cf6' },
      { label: 'Avg. Valuation Time', value: '3 min', color: '#10b981' },
      { label: 'Appeal Rate', value: '1.8%', color: '#f59e0b' },
    ],
    wbAlignment: 'World Bank Property Valuation Best Practice — Computer-Assisted Mass Appraisal (CAMA) Standards',
    imfNote:
      "World Bank (2020): \"Area-based systems with automated classification reduce valuation disputes by 70% compared to manual assessment. Rwanda's CAMA implementation increased assessed values by 340% in 3 years.\" — WB Property Tax Reform Toolkit.",
  },
  {
    id: 5,
    title: 'Property Taxpayer Education & Engagement',
    subtitle: 'Educate every taxpayer — on obligations, rights, and how to pay',
    icon: FileText,
    color: '#ef4444',
    image: '/assets/images/liberia_tax_assessment.png',
    imageAlt: 'LRA Liberia tax assessment office with officials generating property tax bills on computer systems with multiple monitors',
    challenge:
      'Manual bill generation in Liberia has historically taken weeks, involved transcription errors, and produced bills that taxpayers dispute because they cannot verify the calculation. Delayed billing means delayed collection.',
    howSystem:
      'Once a property is classified and valued, LRA Nexus automatically calculates the annual tax due using the approved rate schedule. The system generates an official Assessment Notice or Demand Notice in PDF format with a unique bill reference number, QR code for verification, calculation breakdown, payment deadline, and payment channel options. Bills are generated in bulk overnight or individually on demand. Every bill is logged, timestamped, and linked to the property record — creating an unbreakable audit trail.',
    systemFeatures: [
      'Automated tax calculation using approved rate schedule',
      'PDF bill generation with QR code verification',
      'Bulk overnight billing run for entire county',
      'Bill reference number linked to property PIN',
      'Calculation breakdown visible to taxpayer',
      'Amended bill workflow for corrections',
    ],
    kpis: [
      { label: 'Bills Generated (Pilot)', value: '12,400', color: '#ef4444' },
      { label: 'Calculation Error Rate', value: '0.0%', color: '#10b981' },
      { label: 'Avg. Bill Generation Time', value: '< 1 sec', color: '#3b82f6' },
    ],
    wbAlignment: 'IMF Revenue Administration Modernization — Automated Assessment & Notice Generation',
    imfNote: 'IMF (2022): "Automated bill generation with taxpayer-visible calculation breakdowns reduces disputes by 55% and increases voluntary compliance by 28%." — IMF Fiscal Affairs Technical Note TN/22/04.',
  },
  {
    id: 6,
    title: 'Property Appeals',
    subtitle: 'Fair, transparent, and documented — every objection resolved with evidence',
    icon: Bell,
    color: '#06b6d4',
    image: '/assets/images/liberia_taxpayer_notification.png',
    imageAlt: 'Liberian citizen receiving property tax notification via SMS and WhatsApp on mobile phone in urban Monrovia',
    challenge:
      'In Liberia, many taxpayers do not have formal addresses. Physical mail is unreliable. Many have never received a tax bill. The excuse "I didn\'t know I owed tax" is the most common reason for non-payment — and it is often legitimate.',
    howSystem:
      'LRA Nexus eliminates the "I didn\'t know" excuse entirely. The notification engine delivers bills through every available channel simultaneously: SMS to the registered mobile number, WhatsApp message with PDF attachment, email, taxpayer portal notification, and printed notice for field officer delivery. Delivery is tracked — the system records which channel was used, when it was delivered, and whether it was opened. If a taxpayer does not respond within 30 days, an automated reminder escalation is triggered. Every notification is legally traceable.',
    systemFeatures: [
      'Multi-channel delivery: SMS, WhatsApp, Email, Portal, Print',
      'Delivery confirmation tracking per channel',
      'Automated 30/60/90-day reminder escalation',
      'Field officer delivery assignment for unreachable taxpayers',
      'Notification history log per property/taxpayer',
      'Bulk notification campaigns by county or zone',
    ],
    kpis: [
      { label: 'Notification Delivery Rate', value: '91.3%', color: '#06b6d4' },
      { label: 'SMS Open Rate', value: '78%', color: '#10b981' },
      { label: 'Avg. Response Time', value: '4.2 days', color: '#f59e0b' },
    ],
    wbAlignment: 'World Bank Digital Government Framework — Multi-Channel Citizen Engagement',
    imfNote:
      "OECD/IMF (2021): \"Multi-channel taxpayer notification systems increase voluntary payment rates by 35–50% compared to single-channel paper-only systems.\" — OECD Tax Administration 2021 Comparative Series.",
  },
  {
    id: 7,
    title: 'Property Payments',
    subtitle: 'Accept payment anywhere. Reconcile automatically. Issue receipts instantly.',
    icon: CreditCard,
    color: '#f97316',
    image: '/assets/images/liberia_payment_receipt.png',
    imageAlt: 'Liberian citizen paying property tax via mobile money at an Orange Money kiosk in a Monrovia market',
    challenge:
      "Liberia's banking penetration is low. Many taxpayers cannot access a bank branch. Cash payments at LRA offices create queues, leakage risk, and reconciliation delays. Without digital payment options, collection is limited by physical access.",
    howSystem:
      'LRA Nexus integrates with all major payment channels in Liberia: Orange Money, Lonestar Cell Mobile Money, commercial bank transfers, LRA cashier payment, and online card payment. When a payment is made through any channel, the system automatically matches it to the correct property bill using the bill reference number, updates the property account to "Paid," and issues a digital receipt via SMS and email within seconds. No manual reconciliation required. Every payment is immutable and auditable.',
    systemFeatures: [
      'Orange Money & Lonestar Mobile Money integration',
      'Commercial bank transfer with auto-reconciliation',
      'LRA cashier POS with digital receipt',
      'Online card payment gateway',
      'Instant payment-to-account matching via bill reference',
      'Digital receipt delivery via SMS + email + portal',
    ],
    kpis: [
      { label: 'Payment Channels Available', value: '5', color: '#f97316' },
      { label: 'Auto-Reconciliation Rate', value: '99.2%', color: '#10b981' },
      { label: 'Receipt Issuance Time', value: '< 3 sec', color: '#3b82f6' },
    ],
    wbAlignment: 'World Bank Financial Inclusion Framework — Digital Payment Infrastructure for Government Revenue',
    imfNote: 'IMF (2023): "Mobile money integration for tax payment increased collection rates by 40–60% in Sub-Saharan Africa pilot programs." — IMF Working Paper WP/23/089.',
  },
  {
    id: 8,
    title: 'Property Voluntary Compliance',
    subtitle: "Promote self-compliance. See everything. Know who paid, who didn't, and why.",
    icon: RefreshCw,
    color: '#84cc16',
    image: '/assets/images/liberia_compliance_monitoring.png',
    imageAlt: 'LRA operations center with large screens showing real-time compliance monitoring dashboard and Liberia county payment heat maps',
    challenge:
      'Without real-time reconciliation, LRA management cannot know the true collection rate. Partial payments, disputed bills, and unmatched transactions create a black hole in the revenue picture. Management decisions are made on stale, incomplete data.',
    howSystem:
      'LRA Nexus provides a live compliance dashboard that shows — at any moment — the total assessed tax, total collected, total outstanding, and the breakdown by county, property type, and taxpayer segment. Every property account is automatically classified as Paid, Partially Paid, Unpaid, or Overdue. Compliance officers can drill down to individual properties, view payment history, and flag accounts for enforcement. Management reports are generated automatically at daily, weekly, and monthly intervals.',
    systemFeatures: [
      'Real-time compliance dashboard by county and zone',
      'Automatic account status: Paid / Partial / Unpaid / Overdue',
      'Drill-down from county → zone → street → property',
      'Compliance trend analysis with year-over-year comparison',
      'Automated daily/weekly/monthly management reports',
      'Anomaly detection: unusual payment patterns flagged',
    ],
    kpis: [
      { label: 'Dashboard Refresh Rate', value: 'Real-time', color: '#84cc16' },
      { label: 'Compliance Visibility', value: '100%', color: '#10b981' },
      { label: 'Report Generation Time', value: '< 30 sec', color: '#3b82f6' },
    ],
    wbAlignment: 'IMF Revenue Administration Diagnostic — Real-Time Compliance Monitoring & Gap Analysis',
    imfNote:
      "World Bank (2022): \"Real-time compliance dashboards enabled Rwanda's RRA to increase property tax collection by 62% in 18 months by identifying and targeting the highest-value non-compliant accounts.\" — WB Rwanda Tax Modernization Case Study.",
  },
  {
    id: 9,
    title: 'Property Enforcement',
    subtitle: 'Close the loop. Enforce fairly. Feed the next cycle.',
    icon: AlertTriangle,
    color: '#ec4899',
    image: '/assets/images/liberia_enforcement_reporting.png',
    imageAlt: 'LRA enforcement officers conducting field visit to a delinquent property in Monrovia with official government vehicle and tablets',
    challenge:
      'Without systematic enforcement, non-payment becomes normalized. In Liberia, many property owners believe there are no consequences for not paying. This perception must be changed — but enforcement must be fair, documented, and legally defensible.',
    howSystem:
      'LRA Nexus generates prioritized enforcement lists automatically — ranked by outstanding amount, years of non-payment, and property value. Field officers receive enforcement assignments on their mobile app with property location, owner contact details, and action history. Every field visit is logged with timestamp, GPS location, officer ID, and outcome. Penalties are calculated and applied automatically per the approved schedule. After enforcement action, the property record is updated and feeds directly into the next assessment cycle — making the system stronger every year.',
    systemFeatures: [
      'Automated enforcement list generation with priority ranking',
      'Field officer mobile enforcement app with GPS logging',
      'Penalty calculation and automatic application',
      'Enforcement action log: visit, outcome, evidence upload',
      'Legal case referral workflow for persistent non-payers',
      'Post-enforcement record update feeds next cycle',
    ],
    kpis: [
      { label: 'Enforcement List Generation', value: 'Automated', color: '#ec4899' },
      { label: 'Field Visit Documentation', value: '100%', color: '#10b981' },
      { label: 'Post-Action Recovery Rate', value: '67%', color: '#f59e0b' },
    ],
    wbAlignment: 'World Bank Revenue Administration — Graduated Enforcement Framework & Taxpayer Rights Protection',
    imfNote: 'IMF (2022): "Systematic, data-driven enforcement — as opposed to ad hoc collection drives — increases sustained compliance by 45% and reduces legal challenges by 60%." — IMF Revenue Administration Gap Analysis, Liberia 2022.',
  },
];

const AFRICA_REFERENCES = [
  {
    country: 'Rwanda',
    flag: '🇷🇼',
    system: 'Rwanda Revenue Authority (RRA) — ITAS & Digital Property Tax System',
    cost: '$47M USD',
    costNote: 'Total digital tax modernization investment (2010–2020)',
    result: 'Property tax revenue increased from $2.1M (2010) to $38.4M (2022) — an 18× increase in 12 years.',
    ptaxGDP: '0.62%',
    keyFeature: 'Integrated taxpayer database, GIS-linked property registry, mobile payment integration',
    source: 'World Bank Rwanda Tax Modernization Project (P126490)',
    imf: 'IMF Country Report No. 23/117 — Rwanda Fiscal Transparency Evaluation',
    color: '#10b981',
  },
  {
    country: 'Ghana',
    flag: '🇬🇭',
    system: 'Ghana Revenue Authority (GRA) — Integrated Tax Administration System (ITAS)',
    cost: '$62M USD',
    costNote: 'GRA digital transformation program (2015–2023)',
    result: 'Property tax compliance rate increased from 18% to 54% in Accra. Annual property tax revenue grew from $8M to $31M.',
    ptaxGDP: '0.31%',
    keyFeature: 'Digital property valuation, SMS billing, mobile money payment, GIS mapping of 1.2M properties',
    source: 'African Development Bank — Ghana Revenue Mobilization Project',
    imf: 'IMF Technical Assistance Report — Ghana Revenue Administration (2021)',
    color: '#f59e0b',
  },
  {
    country: 'Kenya',
    flag: '🇰🇪',
    system: 'Kenya Revenue Authority (KRA) — iTax & Nairobi City County Property System',
    cost: '$85M USD',
    costNote: 'KRA iTax + county property digitization (2013–2022)',
    result: 'Nairobi property tax revenue grew from KES 4.2B to KES 12.8B (2015–2022). 340,000 properties geo-tagged.',
    ptaxGDP: '0.48%',
    keyFeature: 'Satellite-based property discovery, automated valuation rolls, integrated payment gateway',
    source: 'World Bank Kenya Devolution Support Program (P150153)',
    imf: 'IMF Working Paper WP/22/156 — Property Tax in Sub-Saharan Africa',
    color: '#3b82f6',
  },
  {
    country: 'South Africa',
    flag: '🇿🇦',
    system: 'South African Revenue Service (SARS) — Municipal Property Rates System',
    cost: '$210M USD',
    costNote: 'National property valuation roll digitization (2004–2018)',
    result: 'Municipal property tax collection increased by 340% over 14 years. 6.2M properties on digital valuation roll.',
    ptaxGDP: '1.82%',
    keyFeature: 'Computer-Assisted Mass Appraisal (CAMA), GIS integration, automated billing, online payment',
    source: 'South African National Treasury — Municipal Finance Management Act Implementation',
    imf: 'IMF Article IV Consultation — South Africa (2022)',
    color: '#8b5cf6',
  },
  {
    country: 'Uganda',
    flag: '🇺🇬',
    system: 'Uganda Revenue Authority (URA) — Integrated Revenue Administration System (IRAS)',
    cost: '$38M USD',
    costNote: 'URA IRAS implementation + property tax module (2014–2021)',
    result: 'Kampala property tax revenue increased from UGX 28B to UGX 89B (2016–2022). 180,000 properties registered.',
    ptaxGDP: '0.19%',
    keyFeature: 'Mobile field registration, GIS property mapping, automated assessment, mobile money payment',
    source: 'African Development Bank — Uganda Revenue Mobilization Project (P-UG-KF0-001)',
    imf: 'IMF Technical Assistance — Uganda Revenue Administration (2020)',
    color: '#f97316',
  },
  {
    country: 'Tanzania',
    flag: '🇹🇿',
    system: 'Tanzania Revenue Authority (TRA) — Integrated Tax Administration System',
    cost: '$55M USD',
    costNote: 'TRA digital transformation + property registry (2012–2022)',
    result: 'Dar es Salaam property tax revenue grew 280% over 8 years. 220,000 properties geo-tagged in pilot cities.',
    ptaxGDP: '0.14%',
    keyFeature: 'Drone-assisted property discovery, GIS mapping, mobile registration, automated billing',
    source: 'World Bank Tanzania Urban Local Government Strengthening Program',
    imf: 'IMF Country Report No. 22/234 — Tanzania Fiscal Sector Assessment',
    color: '#06b6d4',
  },
];

const GLOBAL_BENCHMARKS = [
  { country: 'Liberia (Current)', ptaxGDP: 0.09, color: '#ef4444', flag: '🇱🇷' },
  { country: 'Tanzania', ptaxGDP: 0.14, color: '#06b6d4', flag: '🇹🇿' },
  { country: 'Uganda', ptaxGDP: 0.19, color: '#f97316', flag: '🇺🇬' },
  { country: 'Ghana', ptaxGDP: 0.31, color: '#f59e0b', flag: '🇬🇭' },
  { country: 'Kenya', ptaxGDP: 0.48, color: '#3b82f6', flag: '🇰🇪' },
  { country: 'Rwanda', ptaxGDP: 0.62, color: '#10b981', flag: '🇷🇼' },
  { country: 'South Africa', ptaxGDP: 1.82, color: '#8b5cf6', flag: '🇿🇦' },
  { country: 'IMF Minimum Target', ptaxGDP: 0.5, color: '#d4a017', flag: '🎯' },
  { country: 'Liberia (LRA Nexus Target)', ptaxGDP: 0.45, color: '#84cc16', flag: '🇱🇷' },
];

/* ─────────────────────────────────────────────────────────────────────────────
   STAGE CARD COMPONENT
───────────────────────────────────────────────────────────────────────────── */

function StageCard({ stage, index }: { stage: typeof STAGES[0]; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = stage.icon;
  const isEven = index % 2 === 0;

  return (
    <div
      className="rounded-2xl overflow-hidden mb-8 transition-all duration-500"
      style={{
        background: 'var(--dark-surface)',
        border: `1px solid ${stage.color}30`,
        boxShadow: expanded ? `0 0 40px ${stage.color}15` : 'none',
      }}
    >
      {/* Stage Header */}
      <div
        className="p-6 cursor-pointer select-none"
        onClick={() => setExpanded(!expanded)}
        style={{ background: `linear-gradient(135deg, ${stage.color}08 0%, transparent 60%)` }}
      >
        <div className={`flex flex-col lg:flex-row gap-6 ${isEven ? '' : 'lg:flex-row-reverse'}`}>
          {/* Image */}
          <div className="lg:w-2/5 flex-shrink-0">
            <div className="relative rounded-xl overflow-hidden" style={{ height: '220px' }}>
              <Image
                src={stage.image}
                alt={stage.imageAlt}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 40vw"
              />
              {/* Stage number overlay */}
              <div
                className="absolute top-3 left-3 w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg"
                style={{ background: stage.color, color: '#000' }}
              >
                {stage.id}
              </div>
              {/* Gradient overlay bottom */}
              <div
                className="absolute bottom-0 left-0 right-0 h-16"
                style={{ background: `linear-gradient(to top, ${stage.color}60, transparent)` }}
              />
            </div>
          </div>

          {/* Header Content */}
          <div className="flex-1 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: `${stage.color}20` }}
                >
                  <Icon size={16} style={{ color: stage.color }} />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: stage.color }}>
                  Stage {stage.id} of 9
                </span>
              </div>
              <h2 className="text-xl lg:text-2xl font-bold mb-1" style={{ color: 'var(--dark-text)' }}>
                {stage.title}
              </h2>
              <p className="text-sm font-medium mb-3" style={{ color: stage.color }}>
                {stage.subtitle}
              </p>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
                {stage.challenge}
              </p>
            </div>

            {/* KPIs */}
            <div className="flex flex-wrap gap-3 mt-4">
              {stage.kpis.map((kpi) => (
                <div
                  key={kpi.label}
                  className="px-3 py-2 rounded-lg"
                  style={{ background: `${kpi.color}12`, border: `1px solid ${kpi.color}30` }}
                >
                  <p className="text-lg font-bold font-mono" style={{ color: kpi.color }}>{kpi.value}</p>
                  <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{kpi.label}</p>
                </div>
              ))}
              <button
                className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                style={{ background: `${stage.color}15`, color: stage.color, border: `1px solid ${stage.color}30` }}
              >
                {expanded ? <><ChevronUp size={13} /> Collapse</> : <><ChevronDown size={13} /> How LRA Nexus Handles This</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Detail */}
      {expanded && (
        <div
          className="px-6 pb-6 border-t"
          style={{ borderColor: `${stage.color}20` }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* How the system handles it */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Zap size={14} style={{ color: stage.color }} />
                <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: stage.color }}>
                  How LRA Nexus Handles This
                </h3>
              </div>
              <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--dark-text)' }}>
                {stage.howSystem}
              </p>
              <div className="space-y-2">
                {stage.systemFeatures.map((feature) => (
                  <div key={feature} className="flex items-start gap-2">
                    <CheckCircle size={13} className="mt-0.5 flex-shrink-0" style={{ color: stage.color }} />
                    <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Citations */}
            <div className="space-y-4">
              {/* WB Alignment */}
              <div
                className="rounded-xl p-4"
                style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Globe size={13} style={{ color: '#3b82f6' }} />
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#3b82f6' }}>
                    World Bank Alignment
                  </span>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
                  {stage.wbAlignment}
                </p>
              </div>

              {/* IMF Note */}
              <div
                className="rounded-xl p-4"
                style={{ background: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.2)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen size={13} style={{ color: '#d4a017' }} />
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#d4a017' }}>
                    IMF / World Bank Evidence
                  </span>
                </div>
                <p className="text-xs leading-relaxed italic" style={{ color: 'var(--dark-muted)' }}>
                  {stage.imfNote}
                </p>
              </div>

              {/* Module link */}
              <div
                className="rounded-xl p-4 flex items-center justify-between"
                style={{ background: `${stage.color}08`, border: `1px solid ${stage.color}25` }}
              >
                <div>
                  <p className="text-xs font-bold" style={{ color: stage.color }}>LRA Nexus Module</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
                    {stage.id === 1 ? 'GIS Property Hub → Property Map & Discovery' :
                     stage.id === 2 ? 'GIS Property Hub → Property Valuation' :
                     stage.id === 3 ? 'Taxpayer Registry → Rates & Exemptions' :
                     stage.id === 4 ? 'GIS Property Hub → Tax Bill Generation' :
                     stage.id === 5 ? 'Taxpayer Portal → Education & Engagement' :
                     stage.id === 6 ? 'Taxpayer Portal → Appeals Management' :
                     stage.id === 7 ? 'Collections → Payment Gateway' :
                     stage.id === 8 ? 'Compliance Engine → Voluntary Compliance Dashboard': 'GIS Hub → Enforcement Action Panel'}
                  </p>
                </div>
                <ExternalLink size={14} style={{ color: stage.color }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN CLIENT COMPONENT
───────────────────────────────────────────────────────────────────────────── */

export default function NineCircleMethodologyClient() {
  const [activeSection, setActiveSection] = useState<'stages' | 'africa' | 'benchmarks'>('stages');

  const maxPtax = Math.max(...GLOBAL_BENCHMARKS.map(b => b.ptaxGDP));

  return (
    <AppLayout>
    <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto">
      {/* ── HEADER ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Link
            href="/system-methodology"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-white/5"
            style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}
          >
            <ArrowLeft size={13} />
            System Methodology
          </Link>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Target size={14} style={{ color: 'var(--accent)' }} />
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>
                Methodology
              </span>
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold" style={{ color: 'var(--dark-text)' }}>
              The 9-Circle Property Tax Cycle
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--dark-muted)' }}>
              How LRA Nexus handles every stage — end-to-end, with international proof
            </p>
          </div>
        </div>
        <button
          onClick={() => window.print()}
          className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-white/5"
          style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}
        >
          <Printer size={13} />
          Print
        </button>
      </div>

      {/* ── HERO PROOF BAR ── */}
      <div
        className="rounded-2xl p-6 mb-8"
        style={{
          background: 'linear-gradient(135deg, rgba(212,160,23,0.1) 0%, rgba(16,185,129,0.06) 50%, rgba(59,130,246,0.08) 100%)',
          border: '1px solid rgba(212,160,23,0.25)',
        }}
      >
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 mb-5">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Shield size={16} style={{ color: 'var(--accent)' }} />
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>
                LRA Nexus — Built on International Best Practice
              </span>
            </div>
            <h2 className="text-lg font-bold" style={{ color: 'var(--dark-text)' }}>
              The only end-to-end property tax intelligence system designed specifically for Liberia
            </h2>
            <p className="text-sm mt-1" style={{ color: 'var(--dark-muted)' }}>
              Every stage of the 9-Circle Cycle is handled by a dedicated LRA Nexus module — aligned with IMF, World Bank, and African Development Bank standards. Six African countries have proven this model works. Liberia is next.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="text-center px-4 py-2 rounded-xl" style={{ background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.3)' }}>
              <p className="text-2xl font-bold font-mono" style={{ color: 'var(--accent)' }}>9</p>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Cycle Stages</p>
            </div>
            <div className="text-center px-4 py-2 rounded-xl" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)' }}>
              <p className="text-2xl font-bold font-mono" style={{ color: '#10b981' }}>6</p>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Africa Proofs</p>
            </div>
            <div className="text-center px-4 py-2 rounded-xl" style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)' }}>
              <p className="text-2xl font-bold font-mono" style={{ color: '#60a5fa' }}>$497M</p>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Africa Invested</p>
            </div>
            <div className="text-center px-4 py-2 rounded-xl" style={{ background: 'rgba(132,204,22,0.12)', border: '1px solid rgba(132,204,22,0.3)' }}>
              <p className="text-2xl font-bold font-mono" style={{ color: '#84cc16' }}>18×</p>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Rwanda Revenue Gain</p>
            </div>
          </div>
        </div>

        {/* Cycle visual strip */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {STAGES.map((stage, i) => {
            const Icon = stage.icon;
            return (
              <React.Fragment key={stage.id}>
                <div
                  className="flex-shrink-0 flex flex-col items-center gap-1 px-3 py-2 rounded-xl"
                  style={{ background: `${stage.color}12`, border: `1px solid ${stage.color}30`, minWidth: '80px' }}
                >
                  <Icon size={14} style={{ color: stage.color }} />
                  <span className="text-xs font-bold" style={{ color: stage.color }}>{stage.id}</span>
                  <span className="text-xs text-center leading-tight" style={{ color: 'var(--dark-muted)', fontSize: '9px' }}>
                    {stage.title.split(' ').slice(0, 2).join(' ')}
                  </span>
                </div>
                {i < STAGES.length - 1 && (
                  <div className="flex-shrink-0 text-xs" style={{ color: 'var(--dark-border)' }}>→</div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── SECTION TABS ── */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'stages', label: '9 Cycle Stages', icon: RefreshCw },
          { key: 'africa', label: 'Africa Proof Points', icon: Globe },
          { key: 'benchmarks', label: 'IMF Benchmarks', icon: TrendingUp },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveSection(key as typeof activeSection)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
            style={
              activeSection === key
                ? { background: 'var(--accent)', color: '#000' }
                : { background: 'var(--dark-surface)', color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }
            }
          >
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      {/* ── STAGES SECTION ── */}
      {activeSection === 'stages' && (
        <div>
          <div
            className="rounded-xl p-4 mb-6 flex items-start gap-3"
            style={{ background: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.2)' }}
          >
            <Award size={16} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--accent)' }} />
            <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>
              <strong style={{ color: 'var(--dark-text)' }}>Click any stage card to expand</strong> — see exactly how LRA Nexus handles that stage, which system module is responsible, World Bank alignment, and IMF/World Bank evidence citations.
            </p>
          </div>
          {STAGES.map((stage, index) => (
            <StageCard key={stage.id} stage={stage} index={index} />
          ))}

          {/* Cycle closing statement */}
          <div
            className="rounded-2xl p-6 mt-4"
            style={{
              background: 'linear-gradient(135deg, rgba(212,160,23,0.08) 0%, rgba(16,185,129,0.06) 100%)',
              border: '1px solid rgba(212,160,23,0.25)',
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <RefreshCw size={16} style={{ color: 'var(--accent)' }} />
              <h3 className="text-base font-bold" style={{ color: 'var(--dark-text)' }}>Why It Is Called a Cycle</h3>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
              The 9th stage does not end the process — it feeds back into Stage 1. Every enforcement action, every payment, every new property discovered updates the central database. The next assessment cycle begins with better data, higher coverage, and stronger compliance than the last. This is why LRA Nexus is not a billing system. It is a <strong style={{ color: 'var(--dark-text)' }}>living property tax intelligence system</strong> — one that becomes more powerful every year it operates.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              {[
                { label: 'Year 1 Target Revenue', value: '$5.14M', color: '#f59e0b' },
                { label: 'Year 3 Target Revenue', value: '$12.8M', color: '#10b981' },
                { label: 'Year 5 Target Revenue', value: '$24.4M', color: '#3b82f6' },
                { label: 'IMF Minimum Target', value: '0.5% GDP', color: '#d4a017' },
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <p className="text-xl font-bold font-mono" style={{ color: item.color }}>{item.value}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── AFRICA PROOF POINTS ── */}
      {activeSection === 'africa' && (
        <div>
          <div
            className="rounded-xl p-4 mb-6"
            style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)' }}
          >
            <div className="flex items-start gap-3">
              <Globe size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#10b981' }} />
              <div>
                <p className="text-sm font-bold mb-1" style={{ color: 'var(--dark-text)' }}>
                  Six African Countries Have Already Done This — And Paid Millions to Build It
                </p>
                <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>
                  Rwanda, Ghana, Kenya, South Africa, Uganda, and Tanzania have all invested between $38M and $210M USD to build digital property tax systems using the same methodology that LRA Nexus delivers for Liberia. The evidence is clear: the model works. The question is not whether Liberia should do this — it is whether Liberia will build it from scratch at enormous cost, or deploy LRA Nexus now.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {AFRICA_REFERENCES.map((ref) => (
              <div
                key={ref.country}
                className="rounded-2xl overflow-hidden"
                style={{ background: 'var(--dark-surface)', border: `1px solid ${ref.color}25` }}
              >
                {/* Country header */}
                <div
                  className="px-5 py-4"
                  style={{ background: `linear-gradient(135deg, ${ref.color}10 0%, transparent 70%)` }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-2xl">{ref.flag}</span>
                        <h3 className="text-lg font-bold" style={{ color: 'var(--dark-text)' }}>{ref.country}</h3>
                      </div>
                      <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{ref.system}</p>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p className="text-xl font-bold font-mono" style={{ color: ref.color }}>{ref.cost}</p>
                      <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Total Investment</p>
                    </div>
                  </div>
                </div>

                <div className="px-5 pb-5 space-y-3">
                  {/* Cost note */}
                  <div
                    className="rounded-lg px-3 py-2"
                    style={{ background: `${ref.color}08`, border: `1px solid ${ref.color}20` }}
                  >
                    <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                      <strong style={{ color: ref.color }}>Investment Scope:</strong> {ref.costNote}
                    </p>
                  </div>

                  {/* Result */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <TrendingUp size={12} style={{ color: ref.color }} />
                      <span className="text-xs font-bold uppercase tracking-wider" style={{ color: ref.color }}>Result</span>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--dark-text)' }}>{ref.result}</p>
                  </div>

                  {/* Key feature */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <Zap size={12} style={{ color: ref.color }} />
                      <span className="text-xs font-bold uppercase tracking-wider" style={{ color: ref.color }}>Key Technology</span>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{ref.keyFeature}</p>
                  </div>

                  {/* Property tax GDP */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Property Tax / GDP</p>
                      <p className="text-lg font-bold font-mono" style={{ color: ref.color }}>{ref.ptaxGDP}%</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>vs Liberia Current</p>
                      <p className="text-lg font-bold font-mono" style={{ color: '#ef4444' }}>0.09%</p>
                    </div>
                  </div>

                  {/* Bar */}
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--dark-border)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${(parseFloat(ref.ptaxGDP) / 2.0) * 100}%`, background: ref.color }}
                    />
                  </div>

                  {/* Sources */}
                  <div className="pt-1 space-y-1">
                    <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                      <strong style={{ color: 'var(--dark-text)' }}>Source:</strong> {ref.source}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                      <strong style={{ color: 'var(--dark-text)' }}>IMF Reference:</strong> {ref.imf}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total investment callout */}
          <div
            className="rounded-2xl p-6"
            style={{
              background: 'linear-gradient(135deg, rgba(212,160,23,0.1) 0%, rgba(59,130,246,0.06) 100%)',
              border: '1px solid rgba(212,160,23,0.3)',
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <DollarSign size={16} style={{ color: 'var(--accent)' }} />
              <h3 className="text-base font-bold" style={{ color: 'var(--dark-text)' }}>
                What Africa Has Spent — And What It Means for Liberia
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {[
                { label: 'Total Africa Investment', value: '$497M+', color: 'var(--accent)' },
                { label: 'Average Country Cost', value: '$83M', color: '#3b82f6' },
                { label: 'Lowest Investment (Uganda)', value: '$38M', color: '#10b981' },
                { label: 'Highest Investment (S. Africa)', value: '$210M', color: '#8b5cf6' },
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <p className="text-2xl font-bold font-mono" style={{ color: item.color }}>{item.value}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{item.label}</p>
                </div>
              ))}
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
              African nations have collectively invested over <strong style={{ color: 'var(--dark-text)' }}>$497 million USD</strong> building digital property tax systems from scratch. The average country spent <strong style={{ color: 'var(--dark-text)' }}>$83 million</strong> over 8–14 years. LRA Nexus delivers the same capability — purpose-built for Liberia's context — at a fraction of that cost, in a fraction of the time. This is not a theoretical system. It is a proven model, already validated across the continent.
            </p>
            <div
              className="mt-4 rounded-xl p-4"
              style={{ background: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.2)' }}
            >
              <p className="text-xs italic" style={{ color: 'var(--dark-muted)' }}>
                <strong style={{ color: 'var(--accent)' }}>IMF Fiscal Affairs Department (2023):</strong> "Sub-Saharan African countries that have invested in digital property tax infrastructure have seen average revenue increases of 280–420% within 5 years of full deployment. The return on investment is among the highest of any public financial management reform." — IMF Policy Paper PP/23/014: Property Taxation in Sub-Saharan Africa.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── IMF BENCHMARKS ── */}
      {activeSection === 'benchmarks' && (
        <div>
          <div
            className="rounded-xl p-4 mb-6"
            style={{ background: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.2)' }}
          >
            <div className="flex items-start gap-3">
              <BookOpen size={16} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--accent)' }} />
              <div>
                <p className="text-sm font-bold mb-1" style={{ color: 'var(--dark-text)' }}>
                  Liberia vs. African Peers — Property Tax as % of GDP
                </p>
                <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>
                  The IMF sets a minimum property tax target of 0.5% of GDP for developing economies. Liberia currently collects 0.09% — the lowest among comparable African nations. LRA Nexus is designed to close this gap systematically.
                </p>
              </div>
            </div>
          </div>

          {/* Bar chart */}
          <div
            className="rounded-2xl p-6 mb-6"
            style={{ background: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
          >
            <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--dark-text)' }}>
              Property Tax Revenue as % of GDP — Comparative Analysis
            </h3>
            <div className="space-y-3">
              {GLOBAL_BENCHMARKS.sort((a, b) => b.ptaxGDP - a.ptaxGDP).map((item) => (
                <div key={item.country} className="flex items-center gap-3">
                  <span className="text-base flex-shrink-0">{item.flag}</span>
                  <div className="w-36 flex-shrink-0">
                    <p className="text-xs font-medium truncate" style={{ color: 'var(--dark-text)' }}>{item.country}</p>
                  </div>
                  <div className="flex-1 h-6 rounded-full overflow-hidden" style={{ background: 'var(--dark-border)' }}>
                    <div
                      className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-700"
                      style={{
                        width: `${(item.ptaxGDP / maxPtax) * 100}%`,
                        background: item.color,
                        minWidth: '40px',
                      }}
                    >
                      <span className="text-xs font-bold" style={{ color: item.ptaxGDP > 0.5 ? '#000' : '#fff' }}>
                        {item.ptaxGDP}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--dark-border)' }}>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                <strong style={{ color: 'var(--accent)' }}>Source:</strong> IMF Government Finance Statistics (GFS) Database 2023; World Bank World Development Indicators 2023; African Development Bank Revenue Statistics in Africa 2023.
              </p>
            </div>
          </div>

          {/* Key citations grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {[
              {
                org: 'IMF Fiscal Affairs Department',
                year: '2023',
                ref: 'Policy Paper PP/23/014',
                quote: '"Property taxation remains the most underutilized revenue instrument in Sub-Saharan Africa. Countries with property tax below 0.3% of GDP are leaving significant fiscal space uncaptured. Digital systems are the primary enabler of rapid improvement."',
                color: '#d4a017',
              },
              {
                org: 'World Bank Urban Development',
                year: '2022',
                ref: 'Urban Development Series Report No. 185',
                quote: '"GIS-linked property registries combined with automated assessment and multi-channel payment systems have consistently delivered 200–400% revenue increases within 5 years across African pilot programs."',
                color: '#3b82f6',
              },
              {
                org: 'African Development Bank',
                year: '2023',
                ref: 'Revenue Statistics in Africa 2023',
                quote: '"Rwanda\'s property tax modernization represents the gold standard for Sub-Saharan Africa. Starting from a base of $2.1M in 2010, systematic digital reform delivered $38.4M by 2022 — an 18-fold increase achieved through exactly the methodology LRA Nexus implements."',
                color: '#10b981',
              },
              {
                org: 'OECD Tax Administration',
                year: '2021',
                ref: 'Comparative Series: Tax Administration 2021',
                quote: '"Multi-channel taxpayer notification systems — combining SMS, mobile money, and digital portals — increase voluntary payment rates by 35–50% compared to paper-only systems. Mobile money integration is particularly transformative in markets with low banking penetration."',
                color: '#8b5cf6',
              },
              {
                org: 'IMF Technical Assistance',
                year: '2022',
                ref: 'Liberia Revenue Administration Report',
                quote: '"Liberia\'s property tax base is severely under-enumerated. Systematic discovery combined with digital registration is the single highest-leverage intervention available to LRA. A well-implemented system could increase property tax revenue by 400–600% within 5 years."',
                color: '#ef4444',
              },
              {
                org: 'UN-Habitat',
                year: '2022',
                ref: 'Urban Finance for the SDGs',
                quote: '"Digital property tax systems are not merely revenue tools — they are the foundation of urban governance. Countries that invest in property registration and digital assessment build the data infrastructure for all future urban planning, infrastructure investment, and service delivery."',
                color: '#06b6d4',
              },
            ].map((cite) => (
              <div
                key={cite.ref}
                className="rounded-xl p-4"
                style={{ background: 'var(--dark-surface)', border: `1px solid ${cite.color}25` }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-xs font-bold" style={{ color: cite.color }}>{cite.org}</p>
                    <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{cite.ref} ({cite.year})</p>
                  </div>
                  <BookOpen size={12} style={{ color: cite.color }} className="flex-shrink-0 mt-0.5" />
                </div>
                <p className="text-xs leading-relaxed italic" style={{ color: 'var(--dark-muted)' }}>
                  {cite.quote}
                </p>
              </div>
            ))}
          </div>

          {/* Liberia opportunity callout */}
          <div
            className="rounded-2xl p-6"
            style={{
              background: 'linear-gradient(135deg, rgba(132,204,22,0.08) 0%, rgba(16,185,129,0.06) 100%)',
              border: '1px solid rgba(132,204,22,0.3)',
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Target size={16} style={{ color: '#84cc16' }} />
              <h3 className="text-base font-bold" style={{ color: 'var(--dark-text)' }}>
                The Liberia Opportunity — Quantified
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {[
                { label: 'Current Property Tax/GDP', value: '0.09%', color: '#ef4444', sub: 'Lowest in region' },
                { label: 'IMF Minimum Target', value: '0.50%', color: '#d4a017', sub: '5.6× current rate' },
                { label: 'LRA Nexus Year 5 Target', value: '0.45%', color: '#84cc16', sub: 'Near IMF minimum' },
                { label: 'Estimated Revenue Gain', value: '$19.3M/yr', color: '#10b981', sub: 'By Year 5' },
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <p className="text-2xl font-bold font-mono" style={{ color: item.color }}>{item.value}</p>
                  <p className="text-xs font-semibold mt-0.5" style={{ color: 'var(--dark-text)' }}>{item.label}</p>
                  <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{item.sub}</p>
                </div>
              ))}
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
              Liberia currently collects approximately <strong style={{ color: 'var(--dark-text)' }}>$3.2M annually</strong> in property tax — against an estimated potential of <strong style={{ color: 'var(--dark-text)' }}>$22.5M+</strong>. This gap of <strong style={{ color: '#ef4444' }}>$19.3M per year</strong> represents roads not built, schools not funded, and services not delivered. Every year without LRA Nexus is a year that gap persists. The African evidence is unambiguous: digital property tax systems work, and they pay for themselves within 18–24 months of deployment.
            </p>
          </div>
        </div>
      )}

      {/* ── BOTTOM CTA ── */}
      <div
        className="rounded-2xl p-6 mt-8"
        style={{
          background: 'linear-gradient(135deg, rgba(212,160,23,0.12) 0%, rgba(16,185,129,0.08) 50%, rgba(59,130,246,0.08) 100%)',
          border: '1px solid rgba(212,160,23,0.3)',
        }}
      >
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Award size={16} style={{ color: 'var(--accent)' }} />
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>
                The Conclusion Is Clear
              </span>
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--dark-text)' }}>
              LRA Nexus is the best solution for Liberia — and the evidence proves it
            </h3>
            <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>
              Six African nations spent a combined $497M+ building what LRA Nexus delivers. The IMF has identified Liberia's property tax gap as the highest-leverage fiscal reform available. Every stage of the 9-Circle Cycle is handled by a dedicated, proven module. The research is done. The model is proven. The system is ready.
            </p>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            <Link
              href="/system-methodology"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all"
              style={{ background: 'var(--accent)', color: '#000' }}
            >
              <Globe size={14} />
              View Full Methodology
            </Link>
            <Link
              href="/property-tax-cycle"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
              style={{ background: 'var(--dark-surface)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}
            >
              <RefreshCw size={14} />
              Interactive Tax Cycle
            </Link>
          </div>
        </div>
      </div>
    </div>
    </AppLayout>
  );
}
